import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import {
  blockColorSurface,
  buildCustomWorkoutHeadline,
  buildCustomWorkoutSummaryLine,
  groupStepsByContiguousGroupTitle,
  parseBlockGroupMeta,
  parseGroupColorIndex,
  selectCompletableStepsInBlock,
} from "@cliently/schemas";
import { Button, cn, toast } from "@cliently/ui";
import {
  isStepCompleted,
  useUpdateProgramAssignmentStep,
  type PortalAssignmentSession,
  type PortalBlockMedia,
  type PortalProgramStep,
} from "../../hooks/use-programs";
import { ProgramBlockMedia } from "./ProgramBlockMedia";
import { BlockGuideDialog, type BlockGuideExercise } from "./BlockGuideDialog";

type ProgramDayBlocksProps = {
  sessions: PortalAssignmentSession[];
  clientId: string;
  onOpenStep: (step: PortalProgramStep) => void;
  onMarkModified?: () => void;
};

const Textarea = ({
  className,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    className={cn(
      "border-border/70 bg-background placeholder:text-muted-foreground focus-visible:ring-primary/40 flex w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...rest}
  />
);

type DayBlock = {
  key: string;
  sessionTitle: string | null;
  showSessionTitle: boolean;
  title: string | null;
  /** Entête « type d'entrainement » (EMOM, AMRAP…) avec unités de temps. */
  headline: string | null;
  /** Instruction libre (tempo, consignes…), affichée uniquement en entête. */
  instruction: string | null;
  /** Médias affichés en lecteur intégré (liens, images, vidéos). */
  inlineMedias: PortalBlockMedia[];
  /** Fiches exercice accessibles via le bouton "Guide pratique". */
  guideExercises: BlockGuideExercise[];
  colorIndex: number | null;
  /**
   * Workout structuré en boucle (AMRAP/EMOM/FOR TIME/Circuit training) : le
   * client enchaîne les exercices en boucle, il n'y a donc qu'une validation
   * globale du bloc, jamais de coche par exercice.
   */
  isLoopWorkout: boolean;
  /** Étapes réelles (mutées lors de la validation, incl. lignes techniques). */
  steps: PortalProgramStep[];
  /** Étapes affichées à valider (sans résumé workout ni instruction). */
  visibleSteps: PortalProgramStep[];
};

/** Médias résolus d'un bloc, avec compat legacy (`groupMediaUrl` -> média lien). */
const resolveBlockMedias = (
  head: PortalProgramStep | undefined,
): PortalBlockMedia[] => {
  if (head?.blockMedias && head.blockMedias.length > 0) {
    return head.blockMedias;
  }
  const legacyUrl = head?.groupMediaUrl?.trim();
  if (legacyUrl) {
    return [
      {
        kind: "link",
        url: legacyUrl,
        title: null,
        exerciseId: null,
        exercise: null,
      },
    ];
  }
  return [];
};

const buildDayBlocks = (sessions: PortalAssignmentSession[]): DayBlock[] => {
  const blocks: DayBlock[] = [];
  const multipleSessions = sessions.length > 1;

  for (const session of sessions) {
    const ordered = [...session.steps].sort((a, b) => a.position - b.position);
    const groups = groupStepsByContiguousGroupTitle(ordered);
    groups.forEach((group, index) => {
      const head = group.items[0];
      const meta = parseBlockGroupMeta(head?.groupDescription);
      const headline = meta.workoutType
        ? buildCustomWorkoutHeadline(meta.workoutType, meta.workoutParams ?? {})
        : null;
      const summaryLine = meta.workoutType
        ? buildCustomWorkoutSummaryLine(
            meta.workoutType,
            meta.workoutParams ?? {},
          )
        : null;
      const blockMedias = resolveBlockMedias(head);
      const guideExercises = blockMedias.flatMap((media) =>
        media.kind === "exercise" && media.exercise
          ? [{ title: media.title ?? "", info: media.exercise }]
          : [],
      );
      blocks.push({
        key: `${session.id}:${group.key}:${group.startIndex}`,
        sessionTitle: session.title?.trim() || null,
        showSessionTitle: multipleSessions && index === 0,
        title: group.label,
        headline,
        instruction: meta.instruction,
        inlineMedias: blockMedias.filter(
          (media) => media.kind !== "exercise" && media.url.trim().length > 0,
        ),
        guideExercises,
        colorIndex: parseGroupColorIndex(head?.groupDescription),
        isLoopWorkout: Boolean(meta.workoutType),
        steps: group.items,
        visibleSteps: selectCompletableStepsInBlock(
          group.items,
          summaryLine,
          meta.instruction,
        ),
      });
    });
  }

  return blocks;
};

const stepLabel = (
  step: PortalProgramStep,
  fallback: string,
): string =>
  step.exercise?.nameFr?.trim() ||
  step.exercise?.name ||
  step.title?.trim() ||
  fallback;

export const ProgramDayBlocks = ({
  sessions,
  clientId,
  onOpenStep,
  onMarkModified,
}: ProgramDayBlocksProps) => {
  const { t } = useTranslation();
  const updateStep = useUpdateProgramAssignmentStep();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [guideKey, setGuideKey] = useState<string | null>(null);

  const blocks = buildDayBlocks(sessions);

  const setBlockDone = async (
    block: DayBlock,
    done: boolean,
    note: string | null,
  ) => {
    setSavingKey(block.key);
    try {
      await Promise.all(
        block.steps.map((step, index) =>
          updateStep.mutateAsync({
            id: step.id,
            data:
              index === 0
                ? { isDone: done, valueText: note?.trim() ? note.trim() : null }
                : { isDone: done },
            assignmentClientId: clientId,
          }),
        ),
      );
      onMarkModified?.();
      setEditingKey(null);
      toast.success(
        done
          ? t("programs.blockValidated", "Bloc validé.")
          : t("programs.blockUnvalidated", "Validation annulée."),
      );
    } catch {
      toast.error(
        t("programs.blockValidateError", "Impossible de valider le bloc."),
      );
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-3">
      {blocks.map((block) => {
        const completed =
          block.visibleSteps.length > 0 &&
          block.visibleSteps.every(isStepCompleted);
        const doneCount = block.visibleSteps.filter(isStepCompleted).length;
        const savedNote = block.steps[0]?.valueText?.trim() || null;
        const isEditing = editingKey === block.key;
        const isSaving = savingKey === block.key;
        const surface = blockColorSurface(block.colorIndex);

        return (
          <div key={block.key} className="space-y-1.5">
            {block.showSessionTitle && block.sessionTitle ? (
              <p className="text-muted-foreground px-1 text-xs font-semibold uppercase tracking-[0.12em]">
                {block.sessionTitle}
              </p>
            ) : null}

            <div
              className="overflow-hidden rounded-2xl border border-l-[3px] shadow-xs"
              style={{
                backgroundColor: surface.backgroundColor,
                borderLeft: surface.borderLeft,
                color: surface.color,
              }}
            >
              <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {block.title ? (
                      <h4 className="text-sm font-bold leading-snug">
                        {block.title}
                      </h4>
                    ) : null}
                    {block.headline ? (
                      <p
                        className="mt-0.5 text-xs font-semibold leading-snug"
                        style={{ color: surface.color }}
                      >
                        {block.headline}
                      </p>
                    ) : null}
                    {!block.isLoopWorkout ? (
                      <p
                        className="mt-0.5 text-xs tabular-nums"
                        style={{ color: surface.mutedColor }}
                      >
                        {t("programs.blockProgress", "{{done}}/{{total}} validé(s)", {
                          done: doneCount,
                          total: block.visibleSteps.length,
                        })}
                      </p>
                    ) : null}
                  </div>
                  {completed ? (
                    <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                      {t("programs.blockDone", "Validé")}
                    </span>
                  ) : null}
                </div>

                {block.instruction ? (
                  <p
                    className="whitespace-pre-line text-sm leading-relaxed"
                    style={{ color: surface.mutedColor }}
                  >
                    {block.instruction}
                  </p>
                ) : null}

                {block.inlineMedias.map((media, mediaIdx) => (
                  <ProgramBlockMedia
                    key={`${media.url}-${mediaIdx}`}
                    url={media.url}
                  />
                ))}

                {block.guideExercises.length > 0 ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold shadow-sm transition-all hover:brightness-95 active:scale-[0.98]"
                    style={{
                      backgroundColor: surface.isLight
                        ? "rgba(28, 25, 23, 0.1)"
                        : "rgba(255, 255, 255, 0.18)",
                      border: `1px solid ${
                        surface.isLight
                          ? "rgba(28, 25, 23, 0.22)"
                          : "rgba(255, 255, 255, 0.32)"
                      }`,
                      color: surface.color,
                    }}
                    onClick={() => setGuideKey(block.key)}
                  >
                    <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {t("programs.practicalGuide", "Guide pratique")}
                    {block.guideExercises.length > 1 ? (
                      <span
                        className="rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums"
                        style={{
                          backgroundColor: surface.isLight
                            ? "rgba(28, 25, 23, 0.12)"
                            : "rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        {block.guideExercises.length}
                      </span>
                    ) : null}
                  </button>
                ) : null}

                {block.visibleSteps.length > 0 ? (
                  block.isLoopWorkout ? (
                    <ul className="space-y-1.5">
                      {block.visibleSteps.map((step) => (
                        <li
                          key={step.id}
                          className="flex items-center gap-2.5 px-1 py-0.5"
                          style={{ color: surface.color }}
                        >
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: "currentColor", opacity: 0.5 }}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 text-sm font-medium leading-snug">
                            {stepLabel(
                              step,
                              t(
                                "programs.previewStepUntitled",
                                "Étape sans titre",
                              ),
                            )}
                          </span>
                          {step.targetValue != null ? (
                            <span
                              className="shrink-0 text-xs tabular-nums"
                              style={{ color: surface.mutedColor }}
                            >
                              {step.targetValue} {step.targetUnit ?? ""}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul
                      className="divide-y"
                      style={{
                        borderColor: surface.isLight
                          ? "rgba(28, 25, 23, 0.12)"
                          : "rgba(255, 255, 255, 0.15)",
                      }}
                    >
                    {block.visibleSteps.map((step) => {
                      const stepDone = isStepCompleted(step);
                      return (
                        <li key={step.id}>
                          <button
                            type="button"
                            onClick={() => onOpenStep(step)}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors",
                              surface.isLight
                                ? "hover:bg-black/5"
                                : "hover:bg-white/10",
                            )}
                            style={{ color: surface.color }}
                          >
                            <span
                              className={cn(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                                stepDone
                                  ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-600"
                                  : surface.isLight
                                    ? "border-stone-900/25 bg-white/70"
                                    : "border-white/30 bg-white/10",
                              )}
                            >
                              {stepDone ? (
                                <CheckCircle2 className="h-3 w-3" aria-hidden />
                              ) : null}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span
                                className={cn(
                                  "block text-sm font-medium leading-snug",
                                  stepDone && "line-through opacity-60",
                                )}
                                style={{ color: surface.color }}
                              >
                                {stepLabel(
                                  step,
                                  t(
                                    "programs.previewStepUntitled",
                                    "Étape sans titre",
                                  ),
                                )}
                              </span>
                              {step.targetValue != null ? (
                                <span
                                  className="mt-0.5 block text-xs tabular-nums"
                                  style={{ color: surface.mutedColor }}
                                >
                                  {step.targetValue} {step.targetUnit ?? ""}
                                </span>
                              ) : null}
                            </span>
                            <ChevronRight
                              className="h-4 w-4 shrink-0 opacity-50"
                              style={{ color: surface.color }}
                              aria-hidden
                            />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  )
                ) : null}

                {completed && savedNote && !isEditing ? (
                  <p className="bg-background/60 text-muted-foreground rounded-lg px-3 py-2 text-xs italic leading-relaxed">
                    {savedNote}
                  </p>
                ) : null}

                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      rows={3}
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder={t(
                        "programs.blockNotePlaceholder",
                        "Détails optionnels : ressenti, charges, adaptations…",
                      )}
                    />
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        type="button"
                        variant="outline"
                        className="sm:flex-1"
                        disabled={isSaving}
                        onClick={() => setEditingKey(null)}
                      >
                        {t("common.cancel", "Annuler")}
                      </Button>
                      <Button
                        type="button"
                        className="gap-1.5 sm:flex-1"
                        disabled={isSaving}
                        onClick={() => void setBlockDone(block, true, noteDraft)}
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : null}
                        {t("programs.blockConfirm", "Valider le bloc")}
                      </Button>
                    </div>
                  </div>
                ) : completed ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    disabled={isSaving}
                    onClick={() => void setBlockDone(block, false, savedNote)}
                  >
                    {isSaving ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : null}
                    {t("programs.blockUndo", "Annuler la validation")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="w-full gap-1.5"
                    disabled={isSaving}
                    onClick={() => {
                      setNoteDraft(savedNote ?? "");
                      setEditingKey(block.key);
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                    {t("programs.blockValidate", "Valider le bloc")}
                  </Button>
                )}
              </div>
            </div>

            {block.guideExercises.length > 0 ? (
              <BlockGuideDialog
                open={guideKey === block.key}
                onClose={() => setGuideKey(null)}
                exercises={block.guideExercises}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
